'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { uploadImage, fileToDataURL, prepareImageForUpload } from '@/lib/image-upload';

interface ImageDropzoneProps {
  /** Current image URL */
  currentImageUrl?: string;
  /** Callback when file is ready to upload */
  onUpload: (file: File) => Promise<void>;
  /** Whether upload is in progress */
  uploading?: boolean;
  /** Shape of the dropzone */
  shape?: 'circular' | 'square';
  /** Fallback text for avatar */
  fallbackText?: string;
  /** Maximum width for resizing (default: 800px) */
  maxWidth?: number;
  /** Maximum height for resizing (default: 800px) */
  maxHeight?: number;
  /** Image quality for JPEG (0-1, default: 0.9) */
  quality?: number;
  /** Custom className */
  className?: string;
  /** Enable paste from clipboard */
  enablePaste?: boolean;
}

export function ImageDropzone({
  currentImageUrl,
  onUpload,
  uploading = false,
  shape = 'circular',
  fallbackText = 'U',
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.9,
  className,
  enablePaste = true,
}: ImageDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(currentImageUrl);
  const [error, setError] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  // Update preview when currentImageUrl changes
  useEffect(() => {
    setPreview(currentImageUrl);
  }, [currentImageUrl]);

  const handleFileSelected = useCallback(
    async (file: File) => {
      console.log('[ImageDropzone] File selected:', file.name);
      setError(null);

      try {
        // Show preview immediately
        const dataUrl = await fileToDataURL(file);
        setPreview(dataUrl);
        console.log('[ImageDropzone] Preview set, calling onUpload');

        // Prepare image (resize if needed)
        const preparedFile = await prepareImageForUpload(file, maxWidth, maxHeight, quality);
        console.log('[ImageDropzone] Image prepared, size:', preparedFile.size);

        // Call parent's upload handler
        await onUpload(preparedFile);
        console.log('[ImageDropzone] Upload complete');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        console.error('[ImageDropzone] Upload error:', errorMessage);
        setError(errorMessage);
        setPreview(currentImageUrl);
      }
    },
    [maxWidth, maxHeight, quality, onUpload, currentImageUrl]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const imageFile = files.find((file) => file.type.startsWith('image/'));

      if (imageFile) {
        handleFileSelected(imageFile);
      } else {
        setError('Please drop an image file');
      }
    },
    [handleFileSelected]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelected(file);
      }
      // Reset input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [handleFileSelected]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      if (!enablePaste || !dropzoneRef.current?.contains(document.activeElement)) return;

      const items = Array.from(e.clipboardData?.items || []);
      const imageItem = items.find((item) => item.type.startsWith('image/'));

      if (imageItem) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) {
          handleFileSelected(file);
        }
      }
    },
    [enablePaste, handleFileSelected]
  );

  const handleRemove = useCallback(() => {
    setPreview(undefined);
    setError(null);
  }, []);

  // Set up paste event listener
  useEffect(() => {
    if (enablePaste) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [enablePaste, handlePaste]);

  return (
    <div className={cn('space-y-4', className)}>
      <div
        ref={dropzoneRef}
        className={cn(
          'relative border-2 border-dashed rounded-lg transition-colors',
          shape === 'circular' && 'rounded-full aspect-square',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          uploading && 'pointer-events-none opacity-50',
          !preview && 'cursor-pointer'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !preview && inputRef.current?.click()}
      >
        {preview ? (
          <div
            className="relative group"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <img
              src={preview}
              alt="Preview"
              className={cn(
                'w-full h-full object-cover',
                shape === 'circular' ? 'rounded-full' : 'rounded-lg'
              )}
            />
            {!uploading && isHovering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                <Button
                  variant="destructive"
                  size="icon"
                  className="shadow-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            {uploading ? (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </>
            ) : (
              <>
                <div className="rounded-full bg-muted p-3 mb-4">
                  {isDragging ? (
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  ) : (
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm font-medium mb-1">
                  {isDragging ? 'Drop image here' : 'Drop image here, or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {enablePaste && 'You can also paste (Ctrl+V) • '}
                  PNG, JPG, GIF up to 5MB
                </p>
              </>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
          disabled={uploading}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
